/**
 * 权限工具类
 * 基于 menuComp 直接进行权限控制，移除复杂的权限码映射机制
 *
 * 核心设计：
 * 1. 直接使用 /api/plat/v1/access/menus 返回的菜单数据作为权限数据源
 * 2. 通过 menuComp 字段直接检查权限
 * 3. 基于 menuType 实现精细化权限控制
 * 4. **严格基于页面路径的权限控制**：每个页面的权限独立，只能访问该页面下的权限
 */

// 使用全局API命名空间
type AccessMenusItem = API.AccessMenusItem;

/**
 * 页面权限检查结果
 */
export interface PagePermissionResult {
  /** 总体是否有权限 */
  hasPermission: boolean;
  /** 是否有 menu 类型权限 */
  hasMenu: boolean;
  /** 是否有 page 类型权限 */
  hasPage: boolean;
  /** 缺少的权限类型 */
  missingType?: 'menu' | 'page';
}

/**
 * 权限工具类
 * 基于 menuComp 直接进行权限控制
 */
export class PermissionUtils {
  private static instance: PermissionUtils;
  private userMenus: AccessMenusItem[] = [];
  private menuCompSet: Set<string> = new Set();
  private menuIdSet: Set<string> = new Set();
  private menuPathMap: Map<string, AccessMenusItem> = new Map();

  /** 🔒 新增：页面路径 → 该页面下所有权限码的映射 */
  private pagePermissionMap: Map<string, Set<string>> = new Map();

  private constructor() {}

  /**
   * 获取单例实例
   */
  public static getInstance(): PermissionUtils {
    if (!PermissionUtils.instance) {
      PermissionUtils.instance = new PermissionUtils();
    }
    return PermissionUtils.instance;
  }

  /**
   * 初始化用户权限数据
   * @param menus 用户可访问的菜单列表（来自 /api/plat/v1/access/menus）
   */
  public initUserMenus(menus: AccessMenusItem[]): void {
    this.userMenus = menus;
    this.menuCompSet.clear();
    this.menuIdSet.clear();
    this.menuPathMap.clear();
    this.pagePermissionMap.clear();

    const traverse = (items: AccessMenusItem[], _parentPath?: string) => {
      items.forEach((menu) => {
        // 收集 menuComp
        if (menu.menuComp) {
          this.menuCompSet.add(menu.menuComp);
        }
        // 收集 menuId
        if (menu.menuId) {
          this.menuIdSet.add(menu.menuId);
        }
        // 建立 menuPath 映射
        if (menu.menuPath) {
          this.menuPathMap.set(menu.menuPath, menu);

          // 🔒 关键改进：建立页面路径与权限码的映射关系
          // 将当前菜单及其所有子菜单的权限都关联到该页面路径下
          if (menu.menuPath && menu.menuType !== 'function') {
            const pagePerms = this.getPermissionsForPage(menu.menuPath);
            // 添加当前菜单的权限
            if (menu.menuComp) {
              pagePerms.add(menu.menuComp);
            }
            // 递归收集子菜单的权限
            if (menu.children && menu.children.length > 0) {
              this.collectChildPermissions(menu.children, pagePerms);
            }
            this.pagePermissionMap.set(menu.menuPath, pagePerms);
          }
        }
        // 递归处理子菜单
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children, menu.menuPath);
        }
      });
    };

    traverse(menus);
  }

  /**
   * 🔒 递归收集子菜单的权限码
   */
  private collectChildPermissions(
    children: AccessMenusItem[],
    pagePerms: Set<string>,
  ): void {
    children.forEach((child) => {
      if (child.menuComp) {
        pagePerms.add(child.menuComp);
      }
      if (child.children && child.children.length > 0) {
        this.collectChildPermissions(child.children, pagePerms);
      }
    });
  }

  /**
   * 🔒 获取指定页面路径的权限码集合
   */
  private getPermissionsForPage(pagePath: string): Set<string> {
    // 如果已经存在则返回
    const existing = this.pagePermissionMap.get(pagePath);
    if (existing) {
      return existing;
    }
    // 否则创建新的集合
    return new Set<string>();
  }

  /**
   * 检查是否有指定 menuComp 的权限
   * @param menuComp 菜单组件标识（权限码）
   */
  public hasMenuCompPermission(menuComp: string): boolean {
    return this.menuCompSet.has(menuComp);
  }

  /**
   * 检查是否有指定 menuId 的权限
   * @param menuId 菜单ID
   */
  public hasMenuIdPermission(menuId: string): boolean {
    return this.menuIdSet.has(menuId);
  }

  /**
   * 根据 menuType 获取菜单列表
   * @param menuType 菜单类型（menu/page/function）
   */
  public getMenusByType(menuType: string): AccessMenusItem[] {
    const result: AccessMenusItem[] = [];

    const traverse = (items: AccessMenusItem[]) => {
      items.forEach((menu) => {
        if (menu.menuType === menuType) {
          result.push(menu);
        }
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children);
        }
      });
    };

    traverse(this.userMenus);
    return result;
  }

  /**
   * 根据 menuPath 查找菜单
   * @param menuPath 菜单路径
   */
  public findMenuByPath(menuPath: string): AccessMenusItem | undefined {
    return this.menuPathMap.get(menuPath);
  }

  /**
   * 检查页面访问权限
   * 根据需求：需要同时具备 menu 和 page 类型的权限才能访问页面
   * @param pagePath 页面路径
   */
  public canAccessPage(pagePath: string): PagePermissionResult {
    // 查找对应的 page
    let pageItem: AccessMenusItem | undefined;
    let pageParent: AccessMenusItem | undefined;

    const traverse = (items: AccessMenusItem[], parent?: AccessMenusItem) => {
      items.forEach((menu) => {
        if (menu.menuPath === pagePath) {
          if (menu.menuType === 'page') {
            pageItem = menu;
            pageParent = parent;
          }
        }
        if (menu.children && menu.children.length > 0) {
          traverse(menu.children, menu);
        }
      });
    };

    traverse(this.userMenus);

    const hasPage = !!pageItem;

    // 检查是否有父级 menu 或者自身就是 menu 类型
    // 对于 page 类型的菜单，只要有父级 menu 即可访问
    const hasMenu = pageParent?.menuType === 'menu';

    // 如果找到了 page，且父级是 menu，则有权限
    if (hasPage && hasMenu) {
      return { hasPermission: true, hasMenu: true, hasPage: true };
    }

    // 如果没找到 page，尝试通过路径前缀匹配查找父级
    if (!hasPage) {
      const parentPath = pagePath.split('/').slice(0, -1).join('/') || '/';
      if (parentPath !== pagePath) {
        const parentResult = this.canAccessPage(parentPath);
        if (parentResult.hasPermission) {
          return { hasPermission: true, hasMenu: true, hasPage: true };
        }
      }
    }

    if (!hasPage) {
      return {
        hasPermission: false,
        hasMenu: false,
        hasPage: false,
        missingType: 'page',
      };
    }

    if (!hasMenu) {
      return {
        hasPermission: false,
        hasMenu: false,
        hasPage: true,
        missingType: 'menu',
      };
    }

    return { hasPermission: true, hasMenu: true, hasPage: true };
  }

  /**
   * 检查功能权限（按钮级别）
   * 🔒 严格模式：必须传入页面路径，只在该页面下查找权限
   *
   * @param menuComp 功能组件标识
   * @param pagePath 页面路径（必填）
   */
  public canUseFunction(menuComp: string, pagePath: string): boolean {
    // 🔒 严格限制只在该页面下查找权限
    const pagePerms = this.pagePermissionMap.get(pagePath);
    if (!pagePerms) {
      console.warn(`[PermissionUtils] 页面 ${pagePath} 没有权限配置`);
      return false;
    }
    return pagePerms.has(menuComp);
  }

  /**
   * 批量检查功能权限
   * @param menuComps 功能组件标识列表
   */
  public canUseFunctions(menuComps: string[]): boolean[] {
    return menuComps.map((comp) => this.menuCompSet.has(comp));
  }

  /**
   * 获取用户所有权限码（menuComp 列表）
   */
  public getAllPermissionCodes(): string[] {
    return Array.from(this.menuCompSet);
  }

  /**
   * 获取用户菜单树
   */
  public getUserMenus(): AccessMenusItem[] {
    return this.userMenus;
  }

  /**
   * 根据 menuComp 前缀获取权限列表
   * @param prefix 前缀（如 'function:user:' 或 'btn:user:'）
   */
  public getPermissionsByPrefix(prefix: string): string[] {
    return Array.from(this.menuCompSet).filter((comp) =>
      comp.startsWith(prefix),
    );
  }

  /**
   * 检查是否有指定模块的任意权限
   * @param module 模块名（如 'user', 'system', 'device'）
   */
  public hasModulePermission(module: string): boolean {
    const prefixes = [
      `function:${module}:`,
      `btn:${module}:`,
      `page:${module}:`,
      `menu:${module}:`,
    ];
    return Array.from(this.menuCompSet).some((comp) =>
      prefixes.some((prefix) => comp.startsWith(prefix)),
    );
  }

  /**
   * 获取可访问的菜单树（用于侧边栏显示）
   * 过滤掉 function 类型的菜单
   */
  public getAccessibleMenuTree(): AccessMenusItem[] {
    const filterMenu = (menu: AccessMenusItem): AccessMenusItem | null => {
      // function 类型不显示在侧边栏
      if (menu.menuType === 'function') {
        return null;
      }

      // 检查是否启用
      if (menu.enabled !== '1') {
        return null;
      }

      // 检查是否显示
      if (menu.isShow === '0') {
        return null;
      }

      const filteredMenu: AccessMenusItem = { ...menu };

      // 递归处理子菜单
      if (menu.children && menu.children.length > 0) {
        const filteredChildren = menu.children
          .map((child) => filterMenu(child))
          .filter((child): child is AccessMenusItem => child !== null);

        if (filteredChildren.length > 0) {
          filteredMenu.children = filteredChildren;
        } else {
          delete filteredMenu.children;
        }
      }

      return filteredMenu;
    };

    return this.userMenus
      .map((menu) => filterMenu(menu))
      .filter((menu): menu is AccessMenusItem => menu !== null);
  }

  /**
   * 清除权限数据
   */
  public clear(): void {
    this.userMenus = [];
    this.menuCompSet.clear();
    this.menuIdSet.clear();
    this.menuPathMap.clear();
  }
}

/**
 * 全局权限工具实例
 */
export const permissionUtils = PermissionUtils.getInstance();

/**
 * 便捷函数：检查 menuComp 权限
 */
export const hasMenuComp = (menuComp: string): boolean => {
  return permissionUtils.hasMenuCompPermission(menuComp);
};

/**
 * 便捷函数：检查 menuId 权限
 */
export const hasMenuId = (menuId: string): boolean => {
  return permissionUtils.hasMenuIdPermission(menuId);
};

/**
 * 便捷函数：检查页面访问权限
 */
export const canAccessPage = (pagePath: string): PagePermissionResult => {
  return permissionUtils.canAccessPage(pagePath);
};

/**
 * 便捷函数：检查功能权限
 * 🔒 严格模式：必须传入页面路径
 */
export const canUseFunction = (menuComp: string, pagePath: string): boolean => {
  return permissionUtils.canUseFunction(menuComp, pagePath);
};

export default permissionUtils;
